import {
  ContentLoader,
  DropdownField,
  FormRow,
  MultiselectField,
  PrimaryButton,
  Text,
  TextField,
  useForm,
} from '@weave/design-system';
import { Vertical } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { DataStoreCacheMutations, DataStoreCacheTypes } from 'apis/data-store-cache';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { CountryCodes, countryOptions } from '../../../constants/country-codes';
import { generateEnumOptions } from '../../../helpers/utils';
import { selectCurrentLocationV2 } from '../../../redux/actions/location';
import { InfoTooltip } from 'components/shared/info-tooltip/info-tooltip.component';
import { css } from '@emotion/core';
import { theme } from '@weave/theme';
import { useAlert } from '@weave/alert-system';

const VerticalDropdownOptions = generateEnumOptions(Vertical);
const IntegrationNamesDropdownOptions = generateEnumOptions(
  DataStoreCacheTypes.IntegrationName
);
const PackageBundleDropdownOptions = generateEnumOptions(
  DataStoreCacheTypes.SalesforceAccountProductBundle
);
const BusinessSegmentDropdownOptions = generateEnumOptions(
  DataStoreCacheTypes.SalesforceAccountBusinessSegment
);

export const SetDataStoreForm = () => {
  const currentLocation = useSelector(selectCurrentLocationV2);
  const alerts = useAlert();
  const { mutate: setDataStoreCache, isLoading: isMutating } =
    DataStoreCacheMutations.useSetCustomizationFlagDataStore({
      onSuccess: () => {
        alerts.success('Data store updated successfully');
      },
      onError: () => {
        alerts.error('Error occured while updating data store');
      },
    });

  const { formProps, getFieldProps, seedValues, isComplete } = useForm({
    fields: {
      locationId: {
        type: 'text',
        required: true,
        value: currentLocation?.locationId,
      },
      vertical: {
        type: 'dropdown',
        required: true,
        value: currentLocation?.vertical,
      },
      integrationNames: {
        type: 'multiselect',
        required: true,
        value: [],
      },
      sfPackageBundle: {
        type: 'dropdown',
        required: true,
        value:
          DataStoreCacheTypes.SalesforceAccountProductBundle
            .PRODUCT_BUNDLE_WEAVE_PLUS_ESSENTIALS,
      },
      sfBusinessSegment: {
        type: 'dropdown',
        required: true,
        value:
          DataStoreCacheTypes.SalesforceAccountBusinessSegment.BUSINESS_SEGMENT_SINGLE,
      },
      sfCountryCode: {
        type: 'dropdown',
        required: true,
        value: CountryCodes.USA,
      },
      phoneHomeIconState: {
        type: 'dropdown',
        required: true,
        value: State.ACTIVE,
      },
    },
    onSubmit: (values) => {
      setDataStoreCache({
        locationId: values.locationId,
        vertical: values.vertical as Vertical,
        integrationNames:
          (values.integrationNames as DataStoreCacheTypes.IntegrationName[]) ?? [],
        sfPackageBundle:
          values.sfPackageBundle as DataStoreCacheTypes.SalesforceAccountProductBundle,
        sfBusinessSegment:
          values.sfBusinessSegment as DataStoreCacheTypes.SalesforceAccountBusinessSegment,
        sfCountryCode: values.sfCountryCode as CountryCodes,
        phoneHomeIconState: values.phoneHomeIconState as State,
      });
      console.log('values:', values);
    },
  });

  useEffect(() => {
    if (currentLocation) {
      seedValues({
        locationId: currentLocation.locationId,
        vertical: currentLocation.vertical,
      });
    }
  }, [currentLocation]);

  return (
    <form {...formProps} css={{ position: 'relative', width: 'min(80vh, 600px)' }}>
      <ContentLoader show={isMutating} />
      <div css={styles.formRow}>
        <TextField label="Location ID" {...getFieldProps('locationId')} disabled />
      </div>
      <div css={styles.formRow}>
        <DropdownField label="Vertical" {...getFieldProps('vertical')} disabled>
          {VerticalDropdownOptions.map(({ label, value }) => (
            <DropdownField.Option key={value} value={value as string}>
              {label}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </div>

      <div css={styles.formRow}>
        <DropdownField label="Business Segment" {...getFieldProps('sfBusinessSegment')}>
          {BusinessSegmentDropdownOptions.map(({ label, value }) => (
            <DropdownField.Option key={value} value={value as string}>
              {label}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </div>

      <div css={styles.formRow}>
        <DropdownField label="Product Bundle" {...getFieldProps('sfPackageBundle')}>
          {PackageBundleDropdownOptions.map(({ label, value }) => (
            <DropdownField.Option key={value} value={value as string}>
              {label}
            </DropdownField.Option>
          ))}
        </DropdownField>
        <InfoTooltip
          body={'Non GBB: you can choose Communicate Engage bundles'}
          tooltipPlacement="right"
        />
      </div>

      <div css={styles.formRow}>
        <MultiselectField
          label="Integration Names"
          {...getFieldProps('integrationNames')}
        >
          {IntegrationNamesDropdownOptions.map(({ label, value }) => (
            <MultiselectField.Option key={value} value={value as string}>
              {label}
            </MultiselectField.Option>
          ))}
        </MultiselectField>
        <InfoTooltip
          body={
            <ul>
              <li>PMS Eligible for PA: Dentrix or Opendental</li>
              <li>PMS Eligible for Forms: Opendental</li>
            </ul>
          }
          tooltipPlacement="right"
        />
      </div>

      <div css={styles.formRow}>
        <DropdownField label="Country Code" {...getFieldProps('sfCountryCode')}>
          {countryOptions.map(({ label, value }) => (
            <DropdownField.Option key={value} value={value as string}>
              {label}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </div>

      <div css={styles.formRow}>
        <DropdownField
          label="Phone Home Icon State"
          {...getFieldProps('phoneHomeIconState')}
        >
          <DropdownField.Option key={State.ACTIVE} value={State.ACTIVE}>
            Active
          </DropdownField.Option>
          <DropdownField.Option key={State.HIDE} value={State.HIDE}>
            Hide
          </DropdownField.Option>
        </DropdownField>
      </div>

      <PrimaryButton type="submit" disabled={!isComplete || isMutating}>
        Submit
      </PrimaryButton>
    </form>
  );
};

const styles = {
  formRow: css`
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: ${theme.spacing(1)};
    margin-bottom: ${theme.spacing(2)};
  `,
};
